import { useFormikContext } from 'formik';
import { useCallback, useMemo } from 'react';
import { type FinanceProductFormValues } from '../..';
import { FinanceProductBasedOn } from '../../../../../../api/types';
import {
    prepareWorkbook,
    exportFinanceProductTableExcel,
    prepareFirstRow,
} from '../../../../../../utilities/newVersionExcel';
import { useFinanceProductDetailContext } from '../../../FinanceProductDetailContext';
import useFinanceProductOptions from '../../../shared/useFinanceProductOptions';
import RandomTable from './RandomTable';
import StepTable from './StepTable';

export type InterestRateTableProps = {
    disabled?: boolean;
};

const InterestRateTable = ({ disabled = false }: InterestRateTableProps) => {
    const { values } = useFormikContext<FinanceProductFormValues>();

    const { interestRate, basedOn, displayName } = values;
    const { type, isGenerateStepsInTable, unit } = interestRate || {};

    const { bank } = useFinanceProductDetailContext();
    const { amountUnitOptions } = useFinanceProductOptions(bank?.module?.companyId);

    const mappedHeaders = useMemo(() => {
        const basedOnKey = `${basedOn === FinanceProductBasedOn.DownPayment ? 'Downpayment' : 'Loan'} ${
            amountUnitOptions.find(item => item.value === unit)?.label
        }*`;

        return [
            { text: basedOnKey, key: 'base' },
            { text: 'Term*', key: 'term' },
        ];
    }, [amountUnitOptions, basedOn, unit]);

    const excelColumnKeys = mappedHeaders.map(i => i.text);

    const getSortedAndUnRepeatArray = (values, key) =>
        Array.from(new Set(values.map(i => i[key])))
            .filter(Boolean)
            .sort((a, b) => String(a).localeCompare(String(b)));

    const onExport = useCallback(
        async records => {
            const workbook = await prepareWorkbook();
            const worksheet = workbook.getWorksheet(1);
            const terms = getSortedAndUnRepeatArray(records, 'term');

            prepareFirstRow(worksheet, excelColumnKeys, terms);

            const bases = getSortedAndUnRepeatArray(records, 'base');
            bases.forEach(base => {
                const rowData = [base];
                terms.forEach(term => {
                    const find = records.find(i => i.term === term && i.base === base);
                    rowData.push(find ? find.value : '');
                });
                worksheet.addRow(rowData);
            });

            exportFinanceProductTableExcel(workbook, displayName, 'Interest Rate');
        },
        [displayName, excelColumnKeys]
    );

    const tableProps = {
        disabled,
        mappedHeaders,
        onExport,
    };

    if (type === 'table' && isGenerateStepsInTable) {
        return <StepTable {...tableProps} />;
    }

    return <RandomTable {...tableProps} />;
};

export default InterestRateTable;
